import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, HeartPulse, Microscope, Timer, ShieldCheck, FlaskConical, Sparkles } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const UPCOMING = [
    {
        title: "Full-arch implant rehabilitation",
        tag: "· Implantology",
        desc: "A complete digital full-arch workflow — from photogrammetry scan to screw-retained delivery.",
        icon: FlaskConical,
    },
    {
        title: "Smile makeover with layered veneers",
        tag: "· Aesthetics",
        desc: "Eight anterior veneers designed from a facially-driven digital wax-up.",
        icon: Sparkles,
    },
];

export default function CaseStudies() {
    return (
        <div className="bg-white text-slate-900" data-testid="case-studies-page">
            {/* HERO */}
            <section className="relative bg-[#0B1120] text-white overflow-hidden tr-grain">
                <div className="absolute -top-32 -right-20 w-[420px] h-[420px] tr-glow bg-cyan-500/25" aria-hidden />
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 lg:py-24 relative">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-300/80">· Clinical evidence</div>
                    <h1 className="mt-4 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
                        Case studies.<br />
                        <span className="text-cyan-300 italic">Real patients, real outcomes.</span>
                    </h1>
                    <p className="mt-5 text-white/70 max-w-2xl text-lg leading-[1.7]">
                        Unretouched clinical photography, full treatment timelines, and the engineering decisions behind
                        each restoration — shared with patient consent, identities redacted.
                    </p>
                </div>
            </section>

            {/* FEATURED — PATIENT X */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 lg:py-20" data-testid="cs-featured-patient-x">
                <div className="rounded-3xl border border-slate-200 bg-slate-50/50 overflow-hidden grid lg:grid-cols-12">
                    <div className="lg:col-span-5 p-6 lg:p-8">
                        <BeforeAfterSlider
                            before="/case-study/metal-gap-highlight.jpg"
                            after="/case-study/high-points-intraoral.jpg"
                            beforeLabel="Before · failed metal"
                            afterLabel="After · ceramic seated"
                            className="h-[340px] lg:h-[420px] w-full"
                        />
                        <p className="mt-3 text-[12px] text-slate-500 leading-snug">Drag the handle to compare the failed metal onlay with the seated nano-hybrid ceramic.</p>
                    </div>
                    <div className="lg:col-span-7 p-6 lg:p-10 lg:pe-12 flex flex-col justify-center">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-medium"><HeartPulse size={11} /> Oncology</span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 text-[11px] font-medium"><Microscope size={11} /> Nano-hybrid ceramic</span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-medium">Onlay · 36</span>
                        </div>
                        <h2 className="mt-4 font-display font-light text-3xl sm:text-4xl tracking-tight leading-[1.12]">
                            Two failed metal onlays. A cancer treatment on hold.
                        </h2>
                        <p className="mt-3 text-[15px] text-slate-600 leading-[1.75] max-w-2xl">
                            An immunocompromised patient needed dental clearance before starting cancer therapy — and
                            conventional laboratory work had already failed twice. The double-bonded nano-hybrid ceramic
                            onlay closed the margin, cleared the infection risk, and kept the oncology timeline intact.
                        </p>
                        <div className="mt-6 grid grid-cols-3 gap-3 max-w-lg">
                            {[
                                { icon: Timer, v: "Days", d: "not weeks — scan to seated" },
                                { icon: Microscope, v: "2×", d: "molecular bonding vs single" },
                                { icon: ShieldCheck, v: "10 yr", d: "ToothPassport™ warranty" },
                            ].map((s) => (
                                <div key={s.d} className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <s.icon size={15} className="text-cyan-600" />
                                    <div className="mt-2 font-display text-2xl text-slate-900 leading-none">{s.v}</div>
                                    <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{s.d}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8">
                            <Link to="/case-studies/patient-x" data-testid="cs-read-patient-x"
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white text-[14px] font-medium hover:bg-slate-700 transition">
                                Read the full case study <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* UPCOMING */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-16" data-testid="cs-upcoming">
                <div className="tr-overline mb-5">· In clinic now — publishing soon</div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {UPCOMING.map((c) => (
                        <div key={c.title} className="rounded-3xl border border-dashed border-slate-300 p-7 bg-slate-50/40" data-testid={`cs-upcoming-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">{c.tag}</div>
                                <span className="px-2.5 py-1 rounded-full bg-slate-200/70 text-slate-500 text-[10.5px] font-mono-tr tracking-[0.14em] uppercase">Coming soon</span>
                            </div>
                            <div className="mt-3 flex items-start gap-3">
                                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 text-cyan-300 flex items-center justify-center shrink-0"><c.icon size={17} /></div>
                                <div>
                                    <h3 className="font-display text-xl tracking-tight text-slate-900">{c.title}</h3>
                                    <p className="mt-1 text-[13.5px] text-slate-500 leading-relaxed">{c.desc}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-20">
                <div className="rounded-3xl bg-[#0B1120] text-white tr-grain relative overflow-hidden p-8 lg:p-12 flex flex-wrap items-center justify-between gap-6">
                    <div className="absolute -top-20 right-0 w-72 h-72 tr-glow bg-cyan-500/20" aria-hidden />
                    <div className="relative">
                        <h3 className="font-display font-light text-2xl sm:text-3xl tracking-tight leading-[1.15]">Have a challenging case?</h3>
                        <p className="mt-2 text-[14px] text-slate-400 max-w-xl">Send it to us — if it becomes a published case study, the lab work is on the house.</p>
                    </div>
                    <Link to="/workflow#send-case" data-testid="cs-send-case-cta"
                        className="relative inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] text-[14px] font-medium hover:bg-cyan-300 transition">
                        Send a Case <ArrowUpRight size={15} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
